import Vue from 'vue';
import { errorService } from '@/services/errorService.js'
import { surveyService } from '@/services/surveyService.js';
import { authTokenService } from '@/services/authTokenService.js';
import { CONSTANTS } from '@/common/constants.js';

const validation = {
	// getters, commit, dispatch should be reference methods with namespace: 'validation/{name}'.
	namespaced: true,
	state: {
		/** Temporary field for testing */
		count: 0,
		/** Dictionary key: questionId, value: Array of row level messages */
		errors: {},
		/** Dictionary key: questionId, value: Array of validation banner messages */
		bannerMessages: {},
		/** Dictionary key: questionId, value: Object with questionId, questionDisplayId, questionStatus, isComplete flag */
		questionStatuses: {},
		/** The status of the survey itself. */
		surveyStatus: null,
		/** Flag which locks advanced questions until some criteria has been met. Typically completing some baseline questions. */
		mandatoryQuestionsAnswered: true
	},
	mutations: {
		/**
		 * Stores the error array in the store.validation.errors dictionary with questionId as the key
		 * @params {Object} state The vuex state object
		 * @params {String} questionId The question identifier
		 * @params {Array<object>} errors The array holding the validation items
		 */
		setQuestionErrors (state, { questionId, validationMessages, bannerMessages }) {
			// Must use Vue.set to update the map. Changes cannot be observed on properties that don't exist at initialization
			// Using this Vue.set method adds the item and hooks up the observation.
			Vue.set(state.errors, questionId, validationMessages);
			Vue.set(state.bannerMessages, questionId, bannerMessages);
		},
		/**
		 * Clears any errors from errors and banner messages if the key does not exist in the input array.
		 * Note: When validation messages are returned we only know which question ids have these messages. If the state is holding any
		 * messages for a question id that is not in that set returned from the api then clear them out.
		 * @params {Array<string>} questionIdsWithErrors - An array of question ids that still have errors.
		 */
		clearUnEnumeratedQuestionErrors (state, { questionIdsWithErrors }) {
			for (let questionId in state.errors) {
				if (!questionIdsWithErrors.includes(questionId)) {
					Vue.set(state.errors, questionId, null);
				}
			}
			// Check banner messages separately. Some messages are banner messages only.
			for (let questionId in state.bannerMessages) {
				if (!questionIdsWithErrors.includes(questionId)) {
					Vue.set(state.bannerMessages, questionId, null);
				}
			}
		},
		/**
		 * Stores a question status object in the store dictionary.
		 * @params {Object} state - The vuex state object
		 * @params {String} questionId - The question identifier
		 * @params {Object} questionStatus - The question status object
		 */
		setQuestionStatus (state, { questionId, questionStatus, isComplete }) {
			Vue.set(state.questionStatuses, questionId, questionStatus, isComplete);
		},
		/**
		 * Stores the survey status in the store.
		 * @params {String} surveyStatus - The status of the current survey.
		 */
		setSurveyStatus (state, surveyStatus) {
			state.surveyStatus = surveyStatus;
		},
		/**
		 * Stores the mandatory question answered flag in the store.
		 * @params {Bool} flag - The mandatory question answered flag value.
		 */
		setMandatoryQuestionsAnswered (state, flag) {
			state.mandatoryQuestionsAnswered = flag;
		},
		increment (state) {
			state.count++;
		}
	},
	actions: {
		runValidationsForSurvey (context) {
			errorService.runValidationForSurvey().then(function (response) {
				if (response.data.questions) {
					response.data.questions.forEach(function (item) {
						let payload = {};
						payload.questionId = item.questionId;
						payload.validationMessages = item.validationItems;
						payload.bannerMessages = item.bannerMessages;
						context.commit('setQuestionErrors', payload);
					});
				}
				// Trigger updating the question statuses -- No need to use the namespace when calling locally
				context.dispatch('loadQuestionStatuses')
			});
		},
		saveMandatoryQuestionsAnswered (context, mandatoryQuestionsAnswered) {
			// Persist the value to the store
			context.commit('setMandatoryQuestionsAnswered', mandatoryQuestionsAnswered);
			// Persist the value back to the api
			surveyService.updateMandatoryQuestionsAnswered(authTokenService.getActiveSurveyId(), mandatoryQuestionsAnswered).then(function (response) {
				console.log('Mandatory Questions Answered updated');
			}).catch(function (error) {
				console.error('Mandatory Questions Answered update failed');
				console.error(error);
			});
		},
		/**
		 * Gets all validation messages for the survey and stores them in the store.
		 */
		loadSurveyValidationMessages (context) {
			errorService.getValidationMessagesForSurvey().then(function (response) {
				if (response.data.questions) {
					let questionIdsWithErrors = [];
					response.data.questions.forEach(function (item) {
						let payload = {};
						payload.questionId = item.questionId;
						payload.validationMessages = item.validationItems;
						payload.bannerMessages = item.bannerMessages;
						context.commit('setQuestionErrors', payload);
						questionIdsWithErrors.push(item.questionId.toString());
					});

					context.commit('clearUnEnumeratedQuestionErrors', { questionIdsWithErrors: questionIdsWithErrors });
				}
			});
		},
		/**
		 * Gets all validation messages for a question in the survey and stores them in the store.
		 */
		loadQuestionValidationMessages (context, questionId) {
			debugger;
			errorService.getValidationMessagesForQuestion(questionId).then(function (response) {
				let payload = {};
				payload.questionId = questionId;
				payload.validationMessages = response.data.validationItems;
				payload.bannerMessages = response.data.bannerMessages;
				context.commit('setQuestionErrors', payload);
			});
		},
		loadQuestionStatuses (context) {
			let surveyId = authTokenService.getActiveSurveyId();
			debugger;
			surveyService.getQuestionStatuses(surveyId).then(function (response) {
				context.commit('setSurveyStatus', response.data.surveyStatus);
				context.commit('setMandatoryQuestionsAnswered', response.data.mandatoryQuestionsAnswered);
				response.data.questionStatuses.forEach(function (status) {
					context.commit('setQuestionStatus', { questionId: status.questionId, questionStatus: status });
				});
			}).catch(function (error) {
				console.log('Failed to get question status data for question status icons.');
				console.error(error);
			});
		},
		dismissValidation (context, warningId) {
			var warningIds = [];
			warningIds.push(warningId.id);
			var dismissRequest = {
						surveyMessageIds: warningIds,
						isDismissed: true
					}

			errorService.updateDismissedWarnings(dismissRequest).then(function (response) {
				context.dispatch('loadSurveyValidationMessages');
      });
		},
		showAllDismissed (context, questionInfo) {
			var banners = context.state.bannerMessages[questionInfo.id];
			banners[0].isDismissed = false;
			var warningIds = [];
			context.state.errors[questionInfo.id].forEach(error => {
				if (error.severityType == CONSTANTS.VALIDATION_SEVERITY_TYPE.WARNING && error.isDismissed == true) {
					warningIds.push(error.surveyMessageId);
					error.isDismissed = false;
				}
			})

			if (warningIds.length > 0) {
				var dismissRequest = {
					surveyMessageIds: warningIds,
					isDismissed: false
				}

				errorService.updateDismissedWarnings(dismissRequest).then(function (response) {
					context.dispatch('loadSurveyValidationMessages');
        });
      }

			console.log(dismissRequest)
		}
	},
	getters: {
		/**
		 * Gets the field validation messages for the specified question id and index (field type messages).
		 * @params {string} questionId - The question identifier
		 * @params {Array.<string>} subSectionIds - An array of identifiers for the sub section, e.g. [ "44", "Total" ]
		 * @params {Array.<string>} rowAnswerIds - An array of the anser ids that exist in the row, e.g. [ 'Fy1Con', 'Fy2Con' ]
		 */
    validationMessagesForQuestionRow: (state) => (questionId, subSectionIds, rowAnswerIds) => {
			let ret = [];
			let data = state.errors[questionId];
			// normalize the values used in the comparison
			let answerIdsLower = rowAnswerIds.map(value => value.toLowerCase());
			if (data && data.length > 0) {
				// filter out any undefined elements before attempting to match
				let filteredSubSectionIds = subSectionIds.filter(function (el) {
					return el != null;
				});
				data.forEach(function (err) {
					// contains true when the error field name is in the input rowAnswerIds and if it has a subSectionIds it matches the input
					let contains = answerIdsLower.indexOf(err.field.fieldName.toLowerCase()) >= 0 &&
						(!err.field.subSectionId || filteredSubSectionIds.includes(err.field.subSectionId));
					let isRowErrorOrTrend = CONSTANTS.VALIDATION_DISPLAY_TYPE.FIELD === err.displayType;
					if (contains && isRowErrorOrTrend) {
						ret.push(err);
					}
				});
			}
			return ret;
		},
		/**
		 * Gets the banner validation messages for the specified question id.
		 * @params {string} questionId The question identifier
		 */
		validationMessagesForQuestionBanner: (state) => (questionId) => {
			let data = state.bannerMessages[questionId];
			if (data && data.length > 0) {
				return data;
			}
			return null;
		},
		/**
		 * Gets the status for the specified question id. If we have no data matching the question id return NotStarted.
		 */
		questionStatus: (state) => (questionId) => {
			if (questionId in state.questionStatuses) {
				return state.questionStatuses[questionId].questionStatus;
			} else {
				return 'NotStarted';
			}
		},
		/**
		 * Gets the survey status for the current survey.
		 */
		surveyStatus: state => {
			return state.surveyStatus;
		},
		/**
		 * Gets the mandatory question answered flag for the current survey.
		 */
		mandatoryQuestionsAnswered: (state) => () => {
			return state.mandatoryQuestionsAnswered;
		},
		highlightType: (state) => (questionId, fieldName) => {
			if (state.errors[questionId]) {
				let matchArr = state.errors[questionId].filter(i => i.field && i.field.fieldName.includes(fieldName));
				if (matchArr && matchArr.length) {
					return matchArr[0].severityType;
				}
				return null;
			}

			return false;
		},
		/**
		 * Temporary for testing the store being updated.
		 */
		count: (state) => {
			return state.count;
		},
		isQuestionComplete: (state) => (questionId) => {
			if (questionId in state.questionStatuses) {
				return state.questionStatuses[questionId].isComplete;
			} else {
				return false;
			}
		}
  }
};

export default validation;
